import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Target, Pencil, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SKILL_COLUMNS, type TeamMember } from "./mockData";

// ── Proficiency config ────────────────────────────────────────────────────────

const PROFICIENCY_LABELS = [
  "",
  "Beginner",
  "Basic",
  "Proficient",
  "Advanced",
  "Expert",
];

function proficiencyBg(value: number): string {
  if (value >= 5) return "bg-indigo-500 text-white";
  if (value >= 4) return "bg-indigo-400/80 text-white";
  if (value >= 3)
    return "bg-indigo-300/60 text-indigo-900 dark:text-indigo-100";
  if (value >= 2)
    return "bg-indigo-200/50 text-indigo-800 dark:text-indigo-200";
  if (value >= 1)
    return "bg-indigo-100/40 text-indigo-700 dark:text-indigo-300";
  return "bg-muted text-muted-foreground";
}

// ── Editable cell ─────────────────────────────────────────────────────────────

interface EditCellProps {
  value: number;
  onChange: (v: number) => void;
}

function EditCell({ value, onChange }: EditCellProps) {
  return (
    <div className="inline-flex items-center gap-0.5">
      <button
        className="h-5 w-5 rounded-sm bg-muted/60 hover:bg-muted text-xs font-bold flex items-center justify-center"
        onClick={() => onChange(Math.max(0, value - 1))}
      >
        −
      </button>
      <span
        className={cn(
          "inline-flex items-center justify-center h-6 w-6 rounded text-[11px] font-semibold",
          proficiencyBg(value),
        )}
      >
        {value}
      </span>
      <button
        className="h-5 w-5 rounded-sm bg-muted/60 hover:bg-muted text-xs font-bold flex items-center justify-center"
        onClick={() => onChange(Math.min(5, value + 1))}
      >
        +
      </button>
    </div>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────────

interface SkillMatrixWidgetProps {
  members: TeamMember[];
  isHR?: boolean;
  isLineManager?: boolean;
  onSaveSkills?: (draft: Record<number, Record<string, number>>) => Promise<void>;
}

export function SkillMatrixWidget({
  members,
  isHR = false,
  isLineManager = false,
  onSaveSkills,
}: SkillMatrixWidgetProps) {
  const { t } = useTranslation();

  const [editMode, setEditMode] = useState(false);
  // draft: memberId → skill → value
  const [draft, setDraft] = useState<Record<number, Record<string, number>>>(
    {},
  );

  const canEdit = isHR || isLineManager;

  function enterEdit() {
    const initial: Record<number, Record<string, number>> = {};
    for (const m of members) {
      initial[m.id] = { ...m.skills };
    }
    setDraft(initial);
    setEditMode(true);
  }

  function cancelEdit() {
    setDraft({});
    setEditMode(false);
  }

  const [isSaving, setIsSaving] = useState(false);

  async function saveEdit() {
    if (onSaveSkills) {
      setIsSaving(true);
      try {
        await onSaveSkills(draft);
        setEditMode(false);
      } catch (err) {
        console.error("Failed to save skills", err);
      } finally {
        setIsSaving(false);
      }
    } else {
      setEditMode(false);
    }
  }

  function isCellEditable(member: TeamMember): boolean {
    if (!editMode) return false;
    if (isHR) return true;
    // All members shown are already scoped to the LM's team by the API —
    // no need to filter by lineManagerId (which is null for the leader's own row).
    return isLineManager;
  }

  function setSkill(memberId: number, skill: string, value: number) {
    setDraft((prev) => ({
      ...prev,
      [memberId]: { ...(prev[memberId] ?? {}), [skill]: value },
    }));
  }

  function skillValue(member: TeamMember, skill: string): number {
    if (editMode && draft[member.id] !== undefined) {
      return draft[member.id][skill] ?? member.skills[skill] ?? 0;
    }
    return member.skills[skill] ?? 0;
  }

  const editedMemberCount = useMemo(() => {
    if (!editMode) return 0;
    // All shown members are editable when in edit mode for HR or LM
    return isHR || isLineManager ? members.length : 0;
  }, [editMode, members.length, isHR, isLineManager]);


  const teamAverages = useMemo(() => {
    return SKILL_COLUMNS.map((skill) => {
      const total = members.reduce((sum, m) => {
        const v =
          editMode && draft[m.id] !== undefined
            ? (draft[m.id][skill] ?? m.skills[skill] ?? 0)
            : (m.skills[skill] ?? 0);
        return sum + v;
      }, 0);
      return Math.round((total / (members.length || 1)) * 10) / 10;
    });
  }, [members, draft, editMode]);


  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-violet-500/10">
              <Target className="h-4 w-4 text-violet-400" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold">
                {t("teamOverview.skillMatrix")}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {editMode
                  ? t("teamOverview.editingSkillsFor", {
                      count: editedMemberCount,
                    })
                  : t("teamOverview.skillMatrixSubtitle")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Legend */}
            <div className="hidden lg:flex items-center gap-2 text-[10px] text-muted-foreground">
              {[1, 2, 3, 4, 5].map((v) => (
                <span key={v} className="flex items-center gap-1">
                  <span
                    className={cn(
                      "inline-block h-3 w-3 rounded-sm",
                      proficiencyBg(v).split(" ")[0],
                    )}
                  />
                  {t(`teamOverview.prof${v}`)}
                </span>
              ))}
            </div>

            {canEdit &&
              (editMode ? (
                <div className="flex items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className="text-[10px] border-amber-500/40 text-amber-400 bg-amber-500/10"
                  >
                    {t("teamOverview.draftMode")}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs gap-1"
                    onClick={cancelEdit}
                  >
                    <X className="h-3 w-3" />
                    {t("common.cancel")}
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1 gradient-brand text-primary-foreground"
                    onClick={saveEdit}
                    disabled={isSaving}
                  >
                    <Check className="h-3 w-3" />
                    {isSaving ? t("common.saving", "Saving...") : t("teamOverview.saveSkills")}
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1.5"
                  onClick={enterEdit}
                >
                  <Pencil className="h-3 w-3" />
                  {t("teamOverview.editSkills")}
                </Button>
              ))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr>
              <th className="text-left font-medium text-muted-foreground pb-2 pr-3 min-w-[120px] whitespace-nowrap">
                {t("teamOverview.member")}
              </th>
              {SKILL_COLUMNS.map((skill) => (
                <th
                  key={skill}
                  className="text-center font-medium text-muted-foreground pb-2 px-1 min-w-[80px] whitespace-nowrap"
                >
                  {skill}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {members.map((member, rowIdx) => {
              const editable = isCellEditable(member);
              return (
                <tr
                  key={member.id}
                  className={cn(
                    rowIdx % 2 === 0 ? "" : "bg-muted/20",
                    editable && "ring-1 ring-inset ring-amber-500/20",
                  )}
                >
                  <td className="py-1.5 pr-3">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-primary-foreground shrink-0"
                        style={{ background: member.avatarColor }}
                      >
                        {member.initials}
                      </div>
                      <span className="font-medium truncate max-w-[90px]">
                        {member.name.split(" ")[0]}
                      </span>
                    </div>
                  </td>

                  {SKILL_COLUMNS.map((skill) => {
                    const val = skillValue(member, skill);
                    return (
                      <td key={skill} className="py-1.5 px-1 text-center">
                        {editable ? (
                          <EditCell
                            value={val}
                            onChange={(v) => setSkill(member.id, skill, v)}
                          />
                        ) : (
                          <span
                            title={PROFICIENCY_LABELS[val]}
                            className={cn(
                              "inline-flex items-center justify-center h-6 w-6 rounded text-[11px] font-semibold mx-auto",
                              proficiencyBg(val),
                            )}
                          >
                            {val}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            <tr className="border-t border-border/60">
              <td className="py-1.5 pr-3 font-semibold text-muted-foreground">
                {t("teamOverview.teamAvg")}
              </td>
              {teamAverages.map((avg, i) => (
                <td key={i} className="py-1.5 px-1 text-center">
                  <span
                    className={cn(
                      "inline-flex items-center justify-center h-6 w-6 rounded text-[11px] font-semibold mx-auto",
                      proficiencyBg(Math.round(avg)),
                    )}
                  >
                    {avg.toFixed(1)}
                  </span>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
